import React, { useState, useEffect, useRef } from 'react';
import api from '../api';
import TaskForm from '../components/TaskForm';
import ProposeTaskModal from '../components/ProposeTaskModal';
import TaskDetailModal from '../components/TaskDetailModal';
import FilterSheet from '../components/FilterSheet';
import { SearchField, ActiveStrip, EmptyFiltered, QuickChip, SheetLabel, TimeQuick, ArchiveRow, OptRow, AdvancedToggle, TIME_FUTURE, timeRange, rangeYears, timeLabel, norm, defaultCustom } from '../components/FilterBar';
import { Plus, Send, Clock, Calendar, AlertTriangle, SlidersHorizontal, ChevronDown, ChevronUp, X, ListTree, List, Hand, UserCheck, Play, Check, Mail, User, Users, Search } from 'lucide-react';
import { useTasks } from '../context/TasksContext';

// Opcije filtera "Sudjelovanje" — ikone/boje prate kartice zadataka
// ─── Nadzorna ploča — prigušeni semantički tonovi ───────────────────────────
const NAVY = '#1a2a4a'; // boja navbara — identitet kartice (lijevi rub)
const HUE = {
  pending: '#5b6472', in_progress: '#3a6ea3', done: '#3f8f6b',
  failed: '#ac463b', overdue: '#ac463b',
  proposed: '#a8781f', container: '#5a5f86',
};
const STATUS_ICON = {
  pending: Clock, in_progress: Play, done: Check,
  failed: X, overdue: AlertTriangle,
  proposed: Clock, container: ListTree,
};
const STAMP_WORD = {
  pending: 'Čeka', in_progress: 'U tijeku', done: 'Gotovo',
  failed: 'Neuspješno', overdue: 'Kasni',
  proposed: 'Prijedlog', container: 'Grupa zadataka',
};

// Značka s brojem dana (ili statusnom riječi) na desnom rubu retka — vidi rowBadge()
function rowBadge({ isFailed, taskWorkStatus, isOverdue, isInProgress, diffDays, endDate, now }) {
  if (isFailed) return { text: 'neuspjelo', color: HUE.failed };
  if (taskWorkStatus === 'done') return null;
  if (isOverdue) return { text: 'kasni', color: HUE.overdue };
  if (taskWorkStatus === 'in_progress' || isInProgress) {
    if (!endDate) return null;
    // Kalendarski dani do kraja (ne sati/24): završava danas → „do HH:MM”, sutra → „do sutra”.
    const endDay = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const daysToEnd = Math.round((endDay - today) / 86400000);
    const text = daysToEnd <= 0
      ? `do ${endDate.toLocaleTimeString('hr-HR', { hour: '2-digit', minute: '2-digit' })}`
      : daysToEnd === 1 ? 'do sutra' : `još ${daysToEnd} d`;
    return { text, color: HUE.in_progress };
  }
  if (diffDays === 0) return { text: 'danas', color: HUE.overdue };
  if (diffDays > 0 && diffDays <= 2) return { text: `${diffDays} d`, color: '#a8781f' };
  if (diffDays > 2) return { text: `${diffDays} d`, color: HUE.pending };
  return null;
}

// Kopiraj link + sažetak zadatka u međuspremnik
function copyTaskLink(task, taskCountMode) {
  const url = `${window.location.origin}/?task=${task.id}`;
  const acc = (task.assignments?.filter(a => (taskCountMode === 'accepted' ? a.status === 'accepted' : a.status !== 'declined')).length || 0) + (task.guests?.length || 0);
  const tmp = document.createElement('div');
  tmp.innerHTML = task.description || '';
  const desc = (tmp.textContent || tmp.innerText || '').trim().slice(0, 250);
  const text = `*${task.title} (${acc}/${task.required_people || 1})*${desc ? '\n' + desc : ''}\n${url}`;
  return navigator.clipboard?.writeText(text).catch(() => {
    const el = document.createElement('textarea');
    el.value = text; document.body.appendChild(el); el.select();
    document.execCommand('copy'); document.body.removeChild(el);
  });
}

// ── Long-press na kartici → mini-popis onih koji su prihvatili ─────────────
const AV_COLORS = ['#5b7fb0', '#8a6fae', '#4f9d84', '#b0693f', '#3a6ea3', '#a8781f', '#6c5ce7'];
const avatarColor = (s) => AV_COLORS[[...(s || '?')].reduce((h, c) => h + c.charCodeAt(0), 0) % AV_COLORS.length];
const initialsOf = (n) => (n || '?').trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase();

function WorkDot({ status }) {
  const c = status === 'done' ? '#3f8f6b' : status === 'in_progress' ? '#e17055' : '#c7ccd4';
  const t = status === 'done' ? 'Završeno' : status === 'in_progress' ? 'U tijeku' : 'Nije započeto';
  return <span title={t} style={{ width: 8, height: 8, borderRadius: '50%', background: c, flexShrink: 0 }} />;
}

function RosterPeek({ task, user, reasons = [], onClose }) {
  const accepted = (task.assignments || []).filter(a => a.status === 'accepted');
  const pending = (task.assignments || []).filter(a => a.status === 'pending');
  const guests = task.guests || [];
  const need = task.required_people || 1;
  const total = accepted.length + guests.length;
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(16,24,40,0.4)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 380, maxHeight: '75vh', overflowY: 'auto', background: '#fff', borderRadius: 16, boxShadow: '0 20px 50px rgba(16,24,40,0.35)', padding: 18, animation: 'fadeIn 0.14s ease-out' }}>
        <div style={{ fontSize: 18, fontWeight: 800, color: '#1a2130', lineHeight: 1.3, overflowWrap: 'break-word' }}>{task.title}</div>
        {reasons.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, margin: '12px 0 4px', padding: '11px 13px', borderRadius: 10, background: '#ac463b0f' }}>
            {reasons.map(r => (
              <div key={r} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 15.5, fontWeight: 600, color: '#8f3a31', lineHeight: 1.35 }}>
                <AttentionDot style={{ marginTop: 6 }} />
                <span>{r}</span>
              </div>
            ))}
          </div>
        )}
        <div style={{ fontSize: 14.5, color: '#8a94a6', margin: '10px 0 10px' }}>
          Prihvatili · {total}{!task.is_container && ` / ${need}`}
        </div>
        {task.is_container ? (
          <div style={{ fontSize: 15.5, color: '#8a94a6' }}>Povezani zadatak — sudionici su na podzadacima.</div>
        ) : total === 0 ? (
          <div style={{ fontSize: 15.5, color: '#8a94a6' }}>
            Još nitko nije prihvatio.{pending.length > 0 && ` (${pending.length} na čekanju)`}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {accepted.map((a, i) => (
              <div key={a.email} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '8px 2px', borderTop: i ? '1px solid #f0f2f5' : 'none' }}>
                <span style={{ width: 34, height: 34, borderRadius: '50%', flexShrink: 0, background: avatarColor(a.full_name || a.email), color: '#fff', fontSize: 13, fontWeight: 700, display: 'grid', placeItems: 'center' }}>{initialsOf(a.full_name || a.email)}</span>
                <span style={{ flex: 1, minWidth: 0, fontSize: 16, color: '#2d3436', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {a.full_name || a.email}{a.email === user.email && <span style={{ color: '#8a919c', fontWeight: 400 }}> · ti</span>}
                </span>
                {a.is_mandatory && <AlertTriangle size={15} color="#ac463b" strokeWidth={2.4} />}
                <WorkDot status={a.work_status} />
              </div>
            ))}
            {guests.map((g) => (
              <div key={g.id} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '8px 2px', borderTop: '1px solid #f0f2f5' }}>
                <span style={{ width: 34, height: 34, borderRadius: '50%', flexShrink: 0, background: '#b2bec3', color: '#fff', fontSize: 13, fontWeight: 700, display: 'grid', placeItems: 'center' }}>{initialsOf(g.full_name)}</span>
                <span style={{ flex: 1, fontSize: 16, color: '#2d3436' }}>{g.full_name}</span>
                <span style={{ fontSize: 12.5, color: '#8a94a6', border: '1px solid #dfe6e9', borderRadius: 6, padding: '1px 7px' }}>Gost</span>
              </div>
            ))}
            {pending.length > 0 && (
              <div style={{ fontSize: 14, color: '#a8781f', marginTop: 8, paddingTop: 8, borderTop: '1px solid #f0f2f5' }}>+ {pending.length} na čekanju</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Razlozi pulsirajuće točke na kartici — prikazuju se na dugi pritisak (RosterPeek), iznad sudionika.
// Koriste ga TaskCard (Zadaci) i TaskSwipeCard (Početna).
function taskDotReasons({ task, myAssignment, isPast, isOverdue, isInProgress, diffDays, notEnoughPeople, assignedCount, requiredPeople, canManage }) {
  const reasons = [];
  if (task.status === 'done' || task.is_container) return reasons;
  if (myAssignment?.status === 'pending') reasons.push('Pozvan/a si na zadatak, odgovori na poziv');
  if (myAssignment?.status === 'accepted' && isOverdue && myAssignment?.work_status !== 'done') reasons.push('Rok je prošao, označi da si gotov/a');
  else if (myAssignment?.status === 'accepted' && isPast && myAssignment?.work_status === 'pending') reasons.push('Zadatak je počeo, a još nisi započeo/la');
  if (canManage) {
    const unassignedVolunteers = task.volunteers?.filter(v => !task.assignments?.find(a => a.email === v.email && a.status !== 'declined')).length || 0;
    const pendingRemovals = task.removal_requests?.filter(r => r.status === 'pending').length || 0;
    const comingSoon = !isPast && diffDays <= 7;
    if (unassignedVolunteers) reasons.push(unassignedVolunteers === 1 ? '1 prijava čeka odobrenje' : `${unassignedVolunteers} prijava čeka odobrenje`);
    if (pendingRemovals) reasons.push(pendingRemovals === 1 ? '1 zahtjev za odjavu' : `${pendingRemovals} zahtjeva za odjavu`);
    if (notEnoughPeople && (comingSoon || isInProgress)) {
      const miss = requiredPeople - assignedCount;
      reasons.push(`Fali još ${miss} ${miss === 1 ? 'osoba' : miss < 5 ? 'osobe' : 'osoba'}`);
    }
    if (isOverdue) reasons.push('Rok je prošao, zadatak treba zatvoriti');
  }
  return reasons;
}

// Osobni odnos prema zadatku kao ikona ispod oznake dana (obavezno zamjenjuje sudjeluješ / poziv).
function taskMeMark({ task, myAssignment, iAmParticipating, iVolunteered }) {
  if (task.is_container || task.status === 'proposed') return null;
  if (myAssignment?.is_mandatory && myAssignment.status !== 'declined') return { Icon: AlertTriangle, color: '#ac463b', title: 'Obavezno' };
  if (iAmParticipating) return { Icon: UserCheck, color: '#3f8f6b', title: 'Sudjeluješ' };
  if (iVolunteered) return { Icon: Hand, color: '#3a6ea3', title: 'Prijavljen/a — čeka potvrdu' };
  if (myAssignment?.status === 'pending') return { Icon: Mail, color: '#3a6ea3', title: 'Pozvan/a si' };
  return null;
}

function getTaskWorkStatus(task) {
  if (task.status === 'done') return task.close_result === 'failed' ? 'failed' : 'done';
  if (task.is_container) {
    const subs = task.subtasks || [];
    return subs.some(s => s.status === 'done' || s.work_status === 'in_progress' || s.work_status === 'done') ? 'in_progress' : 'pending';
  }
  const assignments = task.assignments || [];
  if (assignments.length === 0) return 'pending';
  if (assignments.some(a => a.work_status === 'in_progress' || a.work_status === 'done')) return 'in_progress';
  return 'pending';
}

function AttentionDot({ style }) {
  return <span className="attention-dot" title="Potrebna je tvoja akcija" style={style} />;
}

// ─── Prekidač prikaza liste (Stablo / Po datumu) ─────────────────────────────
function ViewSwitcher({ grouped, setGrouped }) {
  const seg = (active) => ({
    display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 11px',
    fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none',
    background: active ? '#1a2a4a' : 'transparent',
    color: active ? '#fff' : '#636e72',
    transition: 'background 0.15s, color 0.15s',
  });
  return (
    <div style={{ display: 'inline-flex', border: '1px solid #e4e8ef', borderRadius: 9, overflow: 'hidden' }}>
      <button type="button" onClick={() => setGrouped(true)} style={seg(grouped)} title="Podzadaci uvučeni pod glavni zadatak">
        <ListTree size={14} /> Stablo
      </button>
      <button type="button" onClick={() => setGrouped(false)} style={seg(!grouped)} title="Ravni popis po datumu">
        <List size={14} /> Po datumu
      </button>
    </div>
  );
}

// ─── FAB Stack ────────────────────────────────────────────────────────────────
function FabStack({ mainButtons, filterButton, hasActiveFilters, onClearFilters, hidden, active }) {
  const [visible, setVisible] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (hidden || active === false) {
      setVisible(false);
    } else {
      timerRef.current = setTimeout(() => setVisible(true), 10);
    }
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [hidden, active]);

  const fabStyle = {
    width: 48, height: 48, borderRadius: 12, border: 'none',
    color: 'white', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 4px 16px rgba(27,61,143,0.35)',
    transition: 'background 0.15s, transform 0.15s, box-shadow 0.15s',
  };

  return (
    <div style={{
      position: 'fixed', right: 16, bottom: 'calc(100px + env(safe-area-inset-bottom))',
      display: 'flex', flexDirection: 'column', gap: 10, zIndex: 150,
      opacity: visible ? 1 : 0,
      transform: visible ? 'scale(1) translateY(0)' : 'scale(0.85) translateY(8px)',
      transition: 'opacity 0.25s ease, transform 0.25s ease',
      pointerEvents: visible ? 'auto' : 'none',
    }}>
      {mainButtons.map((btn, i) => (
        <button key={i} onClick={btn.onClick} title={btn.title}
          style={{ ...fabStyle, background: btn.active ? '#d63031' : '#1a2a4a' }}
        >
          {btn.icon}
        </button>
      ))}

      <div style={{ position: 'relative', width: 48, height: 48 }}>
        {hasActiveFilters && (
          <button onClick={onClearFilters} title="Očisti filtere"
            style={{ ...fabStyle, background: '#d63031', position: 'absolute', right: 58, top: 0, animation: 'fabIn 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)' }}
          >
            <X size={20} />
          </button>
        )}
        <button onClick={filterButton.onClick} title={filterButton.title}
          style={{ ...fabStyle, background: filterButton.active ? '#d63031' : '#1a2a4a', position: 'absolute', right: 0, top: 0 }}
        >
          {filterButton.icon}
          {filterButton.badge > 0 && <span style={{ position: 'absolute', top: -6, right: -6, minWidth: 20, height: 20, borderRadius: 10, background: '#b4552d', color: '#fff', fontSize: 12, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 5px', border: '2px solid #fff' }}>{filterButton.badge}</span>}
        </button>
      </div>
    </div>
  );
}

function TaskCard({ task, user, onClick, taskCountMode, noGap, openSide, setOpenSide, onEdit, onDetails }) {
  const myAssignment = task.assignments?.find(a => a.email === user.email);
  const now = new Date();
  const taskDate = new Date(task.start_date);
  const isPast = taskDate < now;
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const taskDayStart = new Date(taskDate.getFullYear(), taskDate.getMonth(), taskDate.getDate());
  const diffDays = Math.round((taskDayStart - todayStart) / (1000 * 60 * 60 * 24));
  const isRejected = task.status === 'rejected';        // odbijeni prijedlog (vidi ga samo predlagatelj)
  const isProposed = task.status === 'proposed' || isRejected;
  const isMyProposal = task.proposed_by === user.email;
  const taskWorkStatus = getTaskWorkStatus(task);
  const assignedCount = (task.assignments?.filter(a => taskCountMode === 'accepted' ? a.status === 'accepted' : a.status !== 'declined').length || 0) + (task.guests?.length || 0);
  const requiredPeople = task.required_people || 1;
  const iAmParticipating = task.assignments?.some(a => a.email === user.email && a.status === 'accepted');
  const iVolunteered = task.volunteers?.some(v => v.email === user.email)
    && !task.assignments?.some(a => a.email === user.email && a.status !== 'declined');
  const endDate = task.end_date ? new Date(task.end_date) : null;
  const isInProgress = isPast && task.status !== 'done' && (!endDate || endDate > now);
  const isOverdue = isPast && task.status !== 'done' && endDate && endDate < now;

  const isFailed = taskWorkStatus === 'failed';
  const isContainer = task.is_container === true;
  const subTotal = task.subtasks?.length || 0;
  const subDone = task.subtasks?.filter(s => s.status === 'done').length || 0;
  // Napomena: task.start_locked i dalje blokira "u tijeku/gotovo" prije početka (backend),
  // ali se namjerno ne prikazuje kao stanje kartice — korisnik se svejedno može prijaviti/javiti.
  const cardKind = isRejected ? 'failed' : isProposed ? 'proposed'
    : isContainer ? 'container'
    : isFailed ? 'failed'
    : taskWorkStatus === 'done' ? 'done'
    : isOverdue ? 'overdue'
    : (taskWorkStatus === 'in_progress' || isInProgress) ? 'in_progress'
    : 'pending';
  const hue = HUE[cardKind] || HUE.pending;
  const StatusIcon = STATUS_ICON[cardKind] || Clock;
  const badge = !isProposed ? rowBadge({ isFailed, taskWorkStatus, isOverdue, isInProgress, diffDays, endDate, now }) : null;

  const plainDesc = (() => {
    if (!task.description || task.description === '<p></p>') return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = task.description;
    return (tmp.textContent || tmp.innerText || '').trim();
  })();
  const shortDesc = plainDesc.length > 120 ? plainDesc.slice(0, 120) + '…' : plainDesc;
  const descLine = isFailed && task.close_reason
    ? (task.close_reason.length > 120 ? task.close_reason.slice(0, 120) + '…' : task.close_reason)
    : shortDesc;

  const isGroupOwner = user.groups?.some(g => g.group_role === 'owner' && task.groups?.some(tg => tg.id === g.id));
  const canManage = user.role === 'admin' || isGroupOwner;
  const notEnoughPeople = assignedCount < requiredPeople;

  // Prijedlog A: brojka ljudi ide u naslov (isti font)
  const countText = isProposed ? '' : isContainer ? `${subDone}/${subTotal}` : `${assignedCount}/${requiredPeople}`;
  const countColor = isContainer ? '#8a94a6' : notEnoughPeople ? '#a8781f' : '#1a2130';

  const dotReasons = isProposed ? [] : taskDotReasons({ task, myAssignment, isPast, isOverdue, isInProgress, diffDays, notEnoughPeople, assignedCount, requiredPeople, canManage });
  const showDot = dotReasons.length > 0;
  const meMark = taskMeMark({ task, myAssignment, iAmParticipating, iVolunteered });

  // ── Swipe geste (samo mobitel/touch, ne na prijedlozima) ──
  // Povuci lijevo → otkriva Detalji · Podijeli · Uredi. Traži tap na konkretan gumb.
  const swipeEnabled = !isProposed && typeof setOpenSide === 'function';
  const leftActions = swipeEnabled ? [
    { l: 'Detalji', bg: '#55617a', fn: () => { setOpenSide(null); (onDetails || onClick)(); } },
    { l: 'Podijeli', bg: NAVY, fn: () => { setOpenSide(null); copyTaskLink(task, taskCountMode); } },
    ...(canManage ? [{ l: 'Uredi', bg: '#3a6ea3', fn: () => { setOpenSide(null); (onEdit || onClick)(); } }] : []),
  ] : [];
  const leftW = leftActions.length * 52;
  const restX = openSide === 'left' ? -leftW : 0;
  const drag = useRef({ x0: 0, y0: 0, on: false, ax: null });
  const moved = useRef(false);
  const lpTimer = useRef(null);
  const lpStart = useRef({ x: 0, y: 0 });
  const [dx, setDx] = useState(0);
  const [rosterOpen, setRosterOpen] = useState(false);

  const clearLP = () => { if (lpTimer.current) { clearTimeout(lpTimer.current); lpTimer.current = null; } };
  useEffect(() => () => { if (lpTimer.current) clearTimeout(lpTimer.current); }, []);

  const onDown = (e) => {
    if (isProposed) return;
    moved.current = false;
    lpStart.current = { x: e.clientX, y: e.clientY };
    // Dugi pritisak → popis onih koji su prihvatili
    clearLP();
    lpTimer.current = setTimeout(() => {
      lpTimer.current = null;
      moved.current = true;          // proguta naknadni tap
      drag.current.on = false;       // otkaži swipe
      setDx(0);
      if (navigator.vibrate) { try { navigator.vibrate(8); } catch (err) { /* ignore */ } }
      setRosterOpen(true);
    }, 450);
    if (!swipeEnabled || e.pointerType !== 'touch') return;
    drag.current = { x0: e.clientX, y0: e.clientY, on: true, ax: null };
  };
  const onMove = (e) => {
    if (lpTimer.current && (Math.abs(e.clientX - lpStart.current.x) > 10 || Math.abs(e.clientY - lpStart.current.y) > 10)) clearLP();
    const s = drag.current;
    if (!s.on) return;
    const mx = e.clientX - s.x0;
    const my = e.clientY - s.y0;
    if (!s.ax) {
      if (Math.abs(mx) < 10 && Math.abs(my) < 10) return;
      s.ax = Math.abs(mx) > Math.abs(my) ? 'x' : 'y';
      if (s.ax === 'y') { s.on = false; return; }
      moved.current = true;
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }
    }
    const minPos = -leftW - 28;
    const pos = Math.max(minPos, Math.min(0, restX + mx));
    setDx(pos - restX);
  };
  const onUp = () => {
    clearLP();
    const s = drag.current;
    const wasX = s.ax === 'x';
    s.on = false;
    s.ax = null;
    const total = restX + dx;
    setDx(0);
    if (!wasX) return;
    if (total < -Math.max(64, leftW * 0.55)) { setOpenSide('left'); return; }
    if (openSide) setOpenSide(null);
  };
  const handleCardClick = () => {
    if (moved.current) { moved.current = false; return; }
    if (openSide && typeof setOpenSide === 'function') { setOpenSide(null); return; }
    onClick();
  };

  return (
    <>
    <div style={{ position: 'relative', background: '#fff', marginBottom: noGap ? 0 : 8 }}>
      {leftActions.length > 0 && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', justifyContent: 'flex-end' }}>
          {leftActions.map(a => (
            <button key={a.l} onClick={a.fn} style={{ width: 52, border: 0, background: a.bg, color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>{a.l}</button>
          ))}
        </div>
      )}
      <div
        onClick={handleCardClick}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
        onContextMenu={e => e.preventDefault()}
        style={{ position: 'relative', display: 'flex', alignItems: 'flex-start', gap: 12, padding: '18px 10px 18px 8px', background: '#fff', cursor: 'pointer', transform: `translateX(${restX + dx}px)`, transition: drag.current.on ? 'none' : 'transform 0.22s cubic-bezier(0.4,0,0.2,1)', touchAction: 'pan-y', userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none' }}
      >
        <span aria-hidden="true" title={STAMP_WORD[cardKind]} style={{ marginTop: 3, flexShrink: 0, display: 'inline-flex', color: hue }}>
          <StatusIcon size={18} strokeWidth={2.2} />
        </span>

        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 6, minWidth: 0 }}>
            <span style={{ flex: '1 1 auto', minWidth: 0, fontSize: 17.5, fontWeight: 700, color: '#1a2130', lineHeight: 1.35, overflowWrap: 'break-word' }}>
              {task.title}
              <span style={{ marginLeft: 7, fontSize: 15, fontWeight: 700, color: countColor }} title={isContainer ? 'Gotovi / ukupno podzadataka' : 'Prijavljeni / potrebni'}>{countText}</span>
            </span>
          </div>

          <div style={{ fontSize: 14, color: '#8a94a6', display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
            <Calendar size={13} style={{ flexShrink: 0 }} />
            <span className="tab">{taskDate.toLocaleDateString('hr-HR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
            {(isInProgress || isOverdue) && endDate && (
              <span className="tab">→ {endDate.toLocaleDateString('hr-HR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
            )}
          </div>

          {descLine && <div style={{ fontSize: 13.5, color: '#8a94a6', lineHeight: 1.45, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{descLine}</div>}
          {isProposed && <div style={{ fontSize: 13, color: '#8a94a6' }}>{isMyProposal ? 'Tvoj prijedlog' : `Predlaže: ${task.proposed_by_name || task.proposed_by}`}</div>}
        </div>

        {isProposed ? (
          <div style={{ flexShrink: 0, marginTop: 2 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', padding: '4px 9px', borderRadius: 7, fontSize: 13, fontWeight: 800, whiteSpace: 'nowrap', background: `${hue}1f`, color: hue }}>
              {isRejected ? 'Odbijen' : user.role === 'admin' ? 'Pregledaj' : 'Čeka'}
            </span>
          </div>
        ) : (badge || meMark || showDot) ? (
          <div style={{ flexShrink: 0, marginTop: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 7 }}>
            {badge && <span style={{ display: 'inline-flex', alignItems: 'center', padding: '4px 9px', borderRadius: 7, fontSize: 13, fontWeight: 800, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', background: `${badge.color}1f`, color: badge.color }}>{badge.text}</span>}
            {meMark && <span title={meMark.title} style={{ display: 'inline-flex', paddingRight: 6 }}><meMark.Icon size={16} color={meMark.color} strokeWidth={meMark.Icon === AlertTriangle ? 2.4 : 2.2} /></span>}
            {showDot && <AttentionDot style={{ marginRight: 10 }} />}
          </div>
        ) : null}
      </div>
    </div>
    {rosterOpen && <RosterPeek task={task} user={user} reasons={dotReasons} onClose={() => setRosterOpen(false)} />}
    </>
  );
}

function Dashboard({ user, openTaskId, onTaskOpened, taskCountMode = 'assigned', menuOpen, initialFilter = 'all', initialParticipation = null, onResetInitialFilters, active, filterRequest, onFilterSummary, registerSearch, onSearchState }) {
  const { tasksByYear, fetchYear, patchTask } = useTasks();
  const [fetchError, setFetchError] = useState(null);
  const [filter, setFilter] = useState(initialFilter);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [time, setTime] = useState('all');           // 'all' = godina iz Arhive; inače raspon (FilterBar.timeRange)
  const [custom, setCustom] = useState(defaultCustom);
  const [searchOpen, setSearchOpen] = useState(false);
  const [advOpen, setAdvOpen] = useState(false);
  const searchRef = useRef(null);
  const [query, setQuery] = useState('');
  const [fNeed, setFNeed] = useState(false);          // treba ljudi
  const [fMand, setFMand] = useState(false);          // obavezno za mene
  const [fAppr, setFAppr] = useState(false);          // čeka moje odobrenje (admin / vlasnik grupe)
  const [selectedTask, setSelectedTask] = useState(null);
  const [editOnOpen, setEditOnOpen] = useState(false);
  const [detailsOnOpen, setDetailsOnOpen] = useState(false);
  const [swipeOpen, setSwipeOpen] = useState({ id: null, side: null });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showProposeModal, setShowProposeModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [filterParticipation, setFilterParticipation] = useState(initialParticipation ? [initialParticipation] : []);
  const [filterCategories, setFilterCategories] = useState([]);
  const [filterGroups, setFilterGroups] = useState([]);
  const [groupSubtasks, setGroupSubtasks] = useState(() => {
    try { return localStorage.getItem('dashboard_group_subtasks') === '1'; } catch { return false; }
  });
  const [categories, setCategories] = useState([]);
  const [groups, setGroups] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const filterTimerRef = useRef(null);

  const currentYear = new Date().getFullYear();
  const range = timeRange(time, false, custom);
  const neededYears = rangeYears(range, selectedYear);
  const yearsKey = neededYears.join(',');
  const tasks = neededYears.flatMap(y => tasksByYear[y] || []);
  const years = Array.from({ length: 4 }, (_, i) => currentYear - i);

  const statusFilters = [
    { key: 'all', label: 'Otvoreni' },
    { key: 'pending', label: 'Čekaju početak' },
    { key: 'in_progress', label: 'U tijeku' },
    { key: 'done', label: 'Zatvoreni' },
    { key: 'everything', label: 'Svi' },
  ];

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    try { localStorage.setItem('dashboard_group_subtasks', groupSubtasks ? '1' : '0'); } catch { /* ignore */ }
  }, [groupSubtasks]);

  const openFilters = () => {
    if (filterTimerRef.current) { clearTimeout(filterTimerRef.current); filterTimerRef.current = null; }
    setShowFilters(true);
    setFiltersVisible(true);
  };

  const closeFilters = () => {
    if (filterTimerRef.current) return; // zatvaranje već u tijeku
    setFiltersVisible(false);
    filterTimerRef.current = setTimeout(() => { setShowFilters(false); filterTimerRef.current = null; }, 260);
  };

  const toggleFilters = () => showFilters ? closeFilters() : openFilters();

  useEffect(() => {
    api.get('/categories').then(res => setCategories(res.data)).catch(() => {});
    api.get('/groups').then(res => setGroups(res.data)).catch(() => {});
  }, []);
  // Zadaci su keširani po godini — raspon (npr. „30 dana” u prosincu) može trebati i sljedeću godinu
  useEffect(() => { fetchTasks(); }, [yearsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (initialFilter && initialFilter !== 'all') {
      setFilter(initialFilter);
      onResetInitialFilters?.();
    }
  }, [initialFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (initialParticipation !== null) {
      setFilterParticipation([initialParticipation]);
      onResetInitialFilters?.();
    }
  }, [initialParticipation]); // eslint-disable-line react-hooks/exhaustive-deps

  // Dolazak s Grupa / Kategorija: prikaži SVE zadatke te grupe ili kategorije, i zatvorene (ostali filteri se čiste).
  useEffect(() => {
    if (!filterRequest) return;
    setFilter('everything');
    setFilterParticipation([]);
    setFilterGroups(filterRequest.groups || []);
    setFilterCategories(filterRequest.categories || []);
    setTime('all'); setQuery(''); setFNeed(false); setFMand(false); setFAppr(false);
  }, [filterRequest?.nonce]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (openTaskId && tasks.length > 0) {
      const task = tasks.find(t => t.id === openTaskId);
      if (task) { setSelectedTask(task); onTaskOpened(); }
    }
  }, [openTaskId, tasks]);

  const fetchTasks = async () => {
    try {
      await Promise.all(neededYears.map(y => fetchYear(y)));
      setFetchError(null);
    } catch (err) {
      console.error(err);
      setFetchError(err.response?.status === 429
        ? 'Previše zahtjeva u kratkom vremenu. Pokušaj ponovno za par sekundi.'
        : 'Greška pri dohvatu zadataka.');
    }
  };

  const toggleCategory = (id) => setFilterCategories(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]);
  const toggleGroup = (id) => setFilterGroups(prev => prev.includes(id) ? prev.filter(g => g !== id) : [...prev, id]);

  const filterLabel = statusFilters.find(f => f.key === filter)?.label || 'Otvoreni';
  const isAdmin = user.role === 'admin';
  const canApprove = isAdmin || user.groups?.some(g => g.group_role === 'owner');
  const ownsTask = (t) => isAdmin || user.groups?.some(g => g.group_role === 'owner' && t.groups?.some(tg => tg.id === g.id));

  // Vrijeme: zadatak ulazi ako se preklapa s rasponom; „Bilo kada” = cijela godina iz Arhive
  const inTime = (t) => {
    const st = new Date(t.start_date);
    if (!range) return st.getFullYear() === selectedYear;
    const en = t.end_date ? new Date(t.end_date) : st;
    return st < range.to && en >= range.from;
  };
  // Tekst za pretragu (opis je HTML iz editora); kešira se na objektu, cache se mijenja s novim dohvatom
  const searchText = (t) => {
    if (t._search === undefined) {
      const tmp = document.createElement('div');
      tmp.innerHTML = t.description || '';
      Object.defineProperty(t, '_search', { value: norm([t.title, tmp.textContent, ...(t.categories || []).map(c => c.name), ...(t.groups || []).map(g => g.name)].join(' ')), enumerable: false });
    }
    return t._search;
  };
  const q = norm(query.trim());
  const peopleCount = (t) => (t.assignments?.filter(a => (taskCountMode === 'accepted' ? a.status === 'accepted' : a.status !== 'declined')).length || 0) + (t.guests?.length || 0);
  const needsPeople = (t) => t.status !== 'done' && !t.is_container && peopleCount(t) < (t.required_people || 1);
  const isMand = (t) => t.assignments?.some(a => a.email === user.email && a.is_mandatory && a.status !== 'declined');
  const waitsApproval = (t) => ownsTask(t) && (
    t.volunteers?.some(v => !t.assignments?.some(a => a.email === v.email && a.status !== 'declined'))
    || t.removal_requests?.some(r => r.status === 'pending'));

  const proposedTasks = tasks.filter(t => t.status === 'proposed' || (t.status === 'rejected' && t.proposed_by === user.email)).filter(inTime).filter(t => !q || searchText(t).includes(q));
  const activeTasks = tasks.filter(t => t.status === 'active' || t.status === 'done' || t.status === null).filter(inTime);

  const statusOk = (task, f) => {
    if (f === 'all') return task.status !== 'done';
    if (f === 'pending') return task.status !== 'done' && getTaskWorkStatus(task) === 'pending';
    if (f === 'in_progress') return task.status !== 'done' && getTaskWorkStatus(task) === 'in_progress';
    if (f === 'done') return task.status === 'done';
    return true; // 'everything' — otvoreni i zatvoreni
  };
  const matchesStatusFilter = (task) => statusOk(task, filter);

  // Svi filteri; o = privremena promjena za broj na brzom čipu
  const passes = (task, o = {}) => {
    const part = o.part || filterParticipation;
    if (!statusOk(task, o.filter || filter)) return false;
    if (part.length) {
      const isAssigned = task.assignments?.some(a => a.email === user.email && a.status !== 'declined');
      const isInterested = task.volunteers?.some(v => v.email === user.email) && !isAssigned;
      if (!((part.includes('assigned') && isAssigned) || (part.includes('interested') && isInterested))) return false;
    }
    if ((o.need ?? fNeed) && !needsPeople(task)) return false;
    if (fMand && !isMand(task)) return false;
    if ((o.appr ?? fAppr) && !waitsApproval(task)) return false;
    if (filterCategories.length && !task.categories?.some(c => filterCategories.includes(c.id))) return false;
    if (filterGroups.length && !task.groups?.some(g => filterGroups.includes(g.id))) return false;
    if (q && !searchText(task).includes(q)) return false;
    return true;
  };
  // U „Svi” zatvoreni idu na dno, da ne zatrpaju otvorene
  const doneLast = (t) => (filter === 'everything' && t.status === 'done' ? 1 : 0);
  const filteredTasks = activeTasks.filter(t => passes(t)).sort((a, b) => doneLast(a) - doneLast(b) || new Date(a.start_date) - new Date(b.start_date));
  const countWith = (o) => activeTasks.filter(t => passes(t, o)).length;

  const counts = {
    all: activeTasks.filter(t => t.status !== 'done').length,
    pending: activeTasks.filter(t => t.status !== 'done' && getTaskWorkStatus(t) === 'pending').length,
    in_progress: activeTasks.filter(t => t.status !== 'done' && getTaskWorkStatus(t) === 'in_progress').length,
    done: activeTasks.filter(t => t.status === 'done').length,
    everything: activeTasks.length,
  };

  const visibleGroups = user.role === 'admin' ? groups : groups.filter(g => g.members?.some(m => m.email === user.email));

  const resetFilters = () => {
    setFilter('all');
    setSelectedYear(currentYear);
    setTime('all');
    setSearchOpen(false);
    setQuery('');
    setFilterParticipation([]);
    setFNeed(false); setFMand(false); setFAppr(false);
    setFilterCategories([]);
    setFilterGroups([]);
    onResetInitialFilters?.();
  };

  const togglePart = (k) => setFilterParticipation(prev => prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k]);
  const timeText = timeLabel(time, TIME_FUTURE, custom, selectedYear, currentYear);
  const timeOn = time !== 'all' || selectedYear !== currentYear;

  // Sve uključeno, redom kako se čita: traka na stranici, prazno stanje, navbar
  const activeItems = [
    ...(query.trim() ? [{ key: 'q', label: `„${query.trim()}”`, onRemove: () => { setQuery(''); setSearchOpen(false); } }] : []),
    ...(filterParticipation.includes('assigned') ? [{ key: 'my', label: 'Moji', onRemove: () => togglePart('assigned') }] : []),
    ...(fNeed ? [{ key: 'need', label: 'Treba ljudi', onRemove: () => setFNeed(false) }] : []),
    ...(timeOn ? [{ key: 'time', label: timeText, onRemove: () => { setTime('all'); setSelectedYear(currentYear); } }] : []),
    ...(filter !== 'all' ? [{ key: 'st', label: filterLabel, onRemove: () => setFilter('all') }] : []),
    ...(fAppr ? [{ key: 'appr', label: 'Čeka odobrenje', onRemove: () => setFAppr(false) }] : []),
    ...(filterParticipation.includes('interested') ? [{ key: 'int', label: 'Javio sam se', onRemove: () => togglePart('interested') }] : []),
    ...(fMand ? [{ key: 'mand', label: 'Obavezno za mene', onRemove: () => setFMand(false) }] : []),
    ...filterGroups.map(id => ({ key: `g${id}`, label: groups.find(g => g.id === id)?.name || 'Grupa', onRemove: () => toggleGroup(id) })),
    ...filterCategories.map(id => { const c = categories.find(x => x.id === id); return { key: `c${id}`, label: c ? `${c.emoji || ''} ${c.name}`.trim() : 'Kategorija', onRemove: () => toggleCategory(id) }; }),
  ];
  const reasons = activeItems.map(a => a.label);
  // Napredni dio panela: koliko je tamo uključeno (broj na „Napredni filteri”)
  const advCount = (filterParticipation.includes('interested') ? 1 : 0) + (fMand ? 1 : 0) + (filter !== 'all' && filter !== 'in_progress' ? 1 : 0)
    + filterGroups.length + filterCategories.length + (selectedYear !== currentYear ? 1 : 0);

  // Povećalo u navbaru (App.js): fokus u istoj gesti, pa tek onda otvori — iOS inače ne pokaže tipkovnicu
  const openSearch = () => {
    searchRef.current?.focus({ preventScroll: true });
    setSearchOpen(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  // Povećalo je prekidač: drugi dodir zatvara tipkovnicu i polje i briše tekst (isto kao ×)
  const toggleSearch = () => {
    if (searchOpen || query) { searchRef.current?.blur(); setQuery(''); setSearchOpen(false); }
    else openSearch();
  };
  const openSearchRef = useRef(toggleSearch);
  openSearchRef.current = toggleSearch;
  useEffect(() => {
    if (registerSearch) registerSearch('dashboard', () => openSearchRef.current());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Navbar prikaže povećalo kao uključeno dok je pretraga otvorena
  const searchShown = searchOpen || !!query;
  useEffect(() => {
    if (onSearchState) onSearchState('dashboard', searchShown);
  }, [searchShown]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalActiveFilters = reasons.length;

  // Sažetak za stisnuti navbar (samo dok je stranica vidljiva)
  const summaryText = totalActiveFilters ? `${reasons.slice(0, 2).join(' · ')}${reasons.length > 2 ? ` +${reasons.length - 2}` : ''} · ${filteredTasks.length}` : null;
  useEffect(() => {
    if (onFilterSummary) onFilterSummary('dashboard', summaryText);
  }, [summaryText]); // eslint-disable-line react-hooks/exhaustive-deps

  const isGroupOwner = user.groups?.some(g => g.group_role === 'owner');

  // FAB sakriva se kada su otvoreni modali, burger meni ili filter sheet
  const fabHidden = menuOpen || !!selectedTask || showCreateModal || showProposeModal || showFilters;

  // Glavni gumb: jedan „+” za sve. Admin i voditelj → TaskForm (voditelj u njemu može odabrati
  // „Ostalo · prijedlog”), član → prijedlog zadatka.
  const canCreate = user.role === 'admin' || isGroupOwner;
  const openNew = () => (canCreate ? setShowCreateModal(true) : setShowProposeModal(true));
  const mainButtons = [{
    icon: <Plus size={20} />,
    title: canCreate ? 'Novi zadatak' : 'Predloži zadatak',
    onClick: openNew,
  }];

  const filterButton = {
    icon: <SlidersHorizontal size={20} />,
    title: 'Filteri',
    onClick: toggleFilters,
    active: showFilters,
    badge: totalActiveFilters,
  };

  const openTask = (t) => { setEditOnOpen(false); setDetailsOnOpen(false); setSelectedTask(t); };
  const openTaskEditing = (t) => { setEditOnOpen(true); setDetailsOnOpen(false); setSelectedTask(t); };
  const openTaskDetails = (t) => { setEditOnOpen(false); setDetailsOnOpen(true); setSelectedTask(t); };
  const closeTask = () => { setSelectedTask(null); setEditOnOpen(false); setDetailsOnOpen(false); };

  const swipeProps = (t) => ({
    openSide: swipeOpen.id === t.id ? swipeOpen.side : null,
    setOpenSide: (side) => setSwipeOpen(side ? { id: t.id, side } : { id: null, side: null }),
    onEdit: () => openTaskEditing(t),
    onDetails: () => openTaskDetails(t),
  });

  const renderTaskList = () => {
    if (!groupSubtasks) {
      return filteredTasks.map(task => (
        <TaskCard key={task.id} task={task} user={user} onClick={() => openTask(task)} taskCountMode={taskCountMode} {...swipeProps(task)} />
      ));
    }
    const ts = (d) => new Date(d).getTime();
    const openFilterView = filter === 'all' || filter === 'pending' || filter === 'in_progress';
    // U stablu se uvijek prikazuje cijelo stablo: podzadatak prolazi status-filter ILI je završen (u otvorenim pregledima)
    const visibleKid = (k) => matchesStatusFilter(k) || (k.status === 'done' && openFilterView);

    const byId = new Map(activeTasks.map(t => [t.id, t]));
    const kidsByParent = new Map();
    activeTasks.forEach(t => {
      if (t.parent_task_id) {
        const arr = kidsByParent.get(t.parent_task_id) || [];
        arr.push(t);
        kidsByParent.set(t.parent_task_id, arr);
      }
    });
    // Obitelj se prikazuje kao grana čim je bilo tko u njoj prošao PUNI filter (status + sudjelovanje + kategorija + grupa)
    const inView = new Set(filteredTasks.map(t => t.id));

    const handled = new Set();
    const rows = [];

    kidsByParent.forEach((kids, parentId) => {
      const parent = byId.get(parentId);
      if (!parent) return; // roditelj izvan učitane godine → djeca padaju na standalone niže
      const familyInView = inView.has(parentId) || kids.some(k => inView.has(k.id));
      if (!familyInView) return;
      const allKids = kids.slice().sort((a, b) => ts(a.start_date) - ts(b.start_date));
      const wellKids = allKids.filter(visibleKid);
      if (wellKids.length === 0) return; // nema vidljive grane → roditelj (ako je u filteredTasks) ide kao obična kartica niže

      handled.add(parentId);
      wellKids.forEach(k => handled.add(k.id));

      // Poziciju grane određuje najraniji datum među NEZAVRŠENIM članovima (roditelj + otvoreni podzadaci)
      const familyOpen = [parent, ...allKids].filter(x => x.status !== 'done');
      const sortKey = familyOpen.length
        ? Math.min(...familyOpen.map(x => ts(x.start_date)))
        : Math.min(...wellKids.map(x => ts(x.start_date)));

      rows.push({ key: sortKey, last: filter === 'everything' && familyOpen.length === 0 ? 1 : 0, el: (
        <div key={parentId}>
          <TaskCard task={parent} user={user} onClick={() => openTask(parent)} taskCountMode={taskCountMode} noGap {...swipeProps(parent)} />
          <div style={{ marginLeft: 24, paddingLeft: 12, borderLeft: '1.5px solid #eef0f3', marginTop: 8 }}>
            {wellKids.map(st => (
              <TaskCard key={st.id} task={st} user={user} onClick={() => openTask(st)} taskCountMode={taskCountMode} {...swipeProps(st)} />
            ))}
          </div>
        </div>
      ) });
    });

    filteredTasks.forEach(task => {
      if (handled.has(task.id)) return;
      rows.push({ key: ts(task.start_date), last: doneLast(task), el: (
        <TaskCard key={task.id} task={task} user={user} onClick={() => openTask(task)} taskCountMode={taskCountMode} {...swipeProps(task)} />
      ) });
    });

    rows.sort((a, b) => a.last - b.last || a.key - b.key);
    return rows.map(r => r.el);
  };

  return (
    <div>
      {fetchError && (
        <div style={{
          background: '#fff9f9', border: '1px solid #fde8e8', borderLeft: '2px solid #d63031',
          borderRadius: 16, padding: '14px 18px', marginBottom: 16,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <span style={{ fontSize: 14, color: '#636e72' }}>{fetchError}</span>
          <button
            onClick={fetchTasks}
            style={{ flexShrink: 0, padding: '6px 12px', borderRadius: 8, border: 'none', background: '#d63031', color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
          >
            Pokušaj ponovno
          </button>
        </div>
      )}


      {/* ── ACTION BAR — samo desktop ── */}
      {!isMobile && (
        <div style={{ background: 'white', borderRadius: 12, marginBottom: 12, boxShadow: '0 1px 2px rgba(16,24,40,0.05), 0 4px 14px rgba(16,24,40,0.06)', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', padding: '4px 6px', gap: 2 }}>
            <button onClick={openNew} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 14px', border: 'none', borderRadius: 8, background: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600, color: '#1a2a4a', transition: 'background 0.15s' }} onMouseEnter={e => e.currentTarget.style.background = '#f0f4fa'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>
              {canCreate ? <><Plus size={16} color="#1a2a4a" /> Novi zadatak</> : <><Send size={15} color="#1a2a4a" /> Predloži zadatak</>}
            </button>

            <div style={{ marginLeft: 6, display: 'inline-flex' }}>
              <ViewSwitcher grouped={groupSubtasks} setGrouped={setGroupSubtasks} />
            </div>
            <div style={{ flex: 1 }} />
            <button onClick={toggleSearch} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 14px', border: 'none', borderRadius: 8, background: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600, color: '#636e72' }}>
              <Search size={15} /> Pretraži
            </button>
            <button onClick={toggleFilters} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 14px', border: 'none', borderRadius: 8, background: totalActiveFilters > 0 ? '#e8f4fd' : 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600, color: totalActiveFilters > 0 ? '#0984e3' : '#636e72', transition: 'background 0.15s' }} onMouseEnter={e => { if (!totalActiveFilters) e.currentTarget.style.background = '#f8f9fa'; }} onMouseLeave={e => { if (!totalActiveFilters) e.currentTarget.style.background = 'none'; }}>
              <SlidersHorizontal size={15} /> Filteri
              {totalActiveFilters > 0 && <span style={{ background: '#0984e3', color: 'white', borderRadius: '50%', width: 18, height: 18, fontSize: 12, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>{totalActiveFilters}</span>}
              {showFilters ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
          </div>
        </div>
      )}

      {/* ── FILTER SHEET ── */}
      {showFilters && (
        <FilterSheet
          visible={filtersVisible}
          onClose={closeFilters}
          isMobile={isMobile}
          totalActiveFilters={totalActiveFilters}
          resetFilters={resetFilters}
          resultCount={filteredTasks.length}
          resultNoun={{ one: 'zadatak', few: 'zadatka', many: 'zadataka' }}
        >
          <div className="fb-wrap">
            <QuickChip icon={User} label="Moji" on={filterParticipation.includes('assigned')} count={countWith({ part: ['assigned'] })} onClick={() => togglePart('assigned')} />
            <QuickChip icon={Users} label="Treba ljudi" on={fNeed} count={countWith({ need: true })} onClick={() => setFNeed(v => !v)} />
            <QuickChip icon={Play} label="U tijeku" on={filter === 'in_progress'} count={countWith({ filter: 'in_progress' })} onClick={() => setFilter(f => (f === 'in_progress' ? 'all' : 'in_progress'))} />
            {canApprove && <QuickChip icon={Check} label="Čeka odobrenje" on={fAppr} count={countWith({ appr: true }) + (isAdmin ? proposedTasks.filter(t => t.status === 'proposed').length : 0)} onClick={() => setFAppr(v => !v)} />}
          </div>
          <SheetLabel>Vrijeme</SheetLabel>
          <TimeQuick list={TIME_FUTURE} time={time} setTime={setTime} custom={custom} setCustom={setCustom} />

          <AdvancedToggle open={advOpen || advCount > 0} onToggle={() => setAdvOpen(v => !(v || advCount > 0))} activeCount={advCount}>
            <SheetLabel>Za mene</SheetLabel>
            <OptRow title="Javio sam se" sub="Čeka se da te prihvate" on={filterParticipation.includes('interested')} onClick={() => togglePart('interested')} />
            <OptRow title="Obavezno za mene" sub="Označen si kao obavezan sudionik" on={fMand} onClick={() => setFMand(v => !v)} />
            <SheetLabel>Status</SheetLabel>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {statusFilters.map(f => (
                <button key={f.key} onClick={() => setFilter(f.key)} className={`filter-pill${filter === f.key ? ' active' : ''}`}>
                  {f.label}<span style={{ marginLeft: 6, fontSize: 12, opacity: 0.7 }}>({counts[f.key]})</span>
                </button>
              ))}
            </div>
            {visibleGroups.length > 0 && (
              <>
                <SheetLabel>Grupe</SheetLabel>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {visibleGroups.map(g => (
                    <button key={g.id} onClick={() => toggleGroup(g.id)} className={`filter-pill${filterGroups.includes(g.id) ? ' active' : ''}`}>{g.name}</button>
                  ))}
                </div>
              </>
            )}
            {categories.length > 0 && (
              <>
                <SheetLabel>Kategorije</SheetLabel>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {categories.map(c => (
                    <button key={c.id} onClick={() => toggleCategory(c.id)} className={`filter-pill${filterCategories.includes(c.id) ? ' active' : ''}`}>{c.emoji} {c.name}</button>
                  ))}
                </div>
              </>
            )}
            <SheetLabel>Arhiva</SheetLabel>
            <ArchiveRow year={selectedYear} setYear={setSelectedYear} setTime={setTime} years={years} />
          </AdvancedToggle>
        </FilterSheet>
      )}

      <SearchField ref={searchRef} open={searchOpen} query={query} setQuery={setQuery}
        onClose={() => { setQuery(''); setSearchOpen(false); }} placeholder="Pretraži zadatke" />
      <ActiveStrip items={activeItems} count={filteredTasks.length} noun={['zadatak', 'zadatka', 'zadataka']} onReset={resetFilters} />
      {isMobile && (
        <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 10 }}>
          <ViewSwitcher grouped={groupSubtasks} setGrouped={setGroupSubtasks} />
        </div>
      )}

      {/* Predloženi zadaci */}
      {proposedTasks.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#e17055', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Clock size={12} /> {isAdmin ? 'Prijedlozi za pregled' : 'Tvoji prijedlozi'} ({proposedTasks.length})
          </div>
          {proposedTasks.map(task => (
            <TaskCard key={task.id} task={task} user={user} onClick={() => openTask(task)} taskCountMode={taskCountMode} />
          ))}
        </div>
      )}

      {filteredTasks.length === 0 ? (
        <EmptyFiltered noun="zadataka" reasons={reasons} onReset={resetFilters} />
      ) : (
        renderTaskList()
      )}

      {/* ── FAB Stack — samo mobile ── */}
      {isMobile && (
        <FabStack
          mainButtons={mainButtons}
          filterButton={filterButton}
          hasActiveFilters={totalActiveFilters > 0}
          onClearFilters={resetFilters}
          hidden={fabHidden}
          active={active}
        />
      )}

      {selectedTask && (
        <TaskDetailModal task={selectedTask} user={user} onClose={closeTask}
          onUpdate={(shouldClose, patch) => { if (patch) patchTask(patch); else fetchTasks(); if (shouldClose) closeTask(); }}
          taskCountMode={taskCountMode}
          startEditing={editOnOpen}
          startDetailsOpen={detailsOnOpen}
        />
      )}
      {showCreateModal && <TaskForm user={user} onClose={() => setShowCreateModal(false)} onSaved={() => { fetchTasks(); setShowCreateModal(false); }} />}
      {showProposeModal && <ProposeTaskModal onClose={() => setShowProposeModal(false)} onSave={() => { fetchTasks(); setShowProposeModal(false); }} />}
    </div>
  );
}

export { RosterPeek, taskDotReasons, taskMeMark };
export default Dashboard;
